import React, { useState, useEffect, useMemo } from 'react';
import { FaTimes, FaUser, FaTshirt, FaTrash, FaPlus, FaSearch } from 'react-icons/fa';
import './TeamDetailsModal.css';
import API_BASE_URL from '../../config/api';

const TeamDetailsModal = ({ isOpen, onClose, teamData, isEditable = false, onTeamUpdate, athletePayments = null }) => {
    const [addingAthletes, setAddingAthletes] = useState(false);
    const [availableAthletes, setAvailableAthletes] = useState([]);
    const [selectedAthletes, setSelectedAthletes] = useState([]);
    const [athleteSearch, setAthleteSearch] = useState('');
    const [athletesLoading, setAthletesLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (isOpen && isEditable && addingAthletes) {
            fetchAvailableAthletes();
        }
    }, [isOpen, isEditable, addingAthletes]);

    const fetchAvailableAthletes = async () => {
        setAthletesLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/api/users?profileType=athlete&isActive=true&pageSize=1000`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setAvailableAthletes(Array.isArray(data) ? data : (data.items ?? []));
            }
        } catch (error) {
            console.error('Error fetching athletes:', error);
        } finally {
            setAthletesLoading(false);
        }
    };

    const getEmailBase = (email) => {
        if (!email) return '';
        const [localPart, domain] = email.split('@');
        if (!localPart || !domain) return email;
        const [base] = localPart.split('+');
        return `${base}@${domain}`;
    };

    const filteredAthletes = useMemo(() => {
        const term = athleteSearch.toLowerCase().trim();
        return availableAthletes.filter(athlete => {
            const isInTeam = teamData?.athletes?.some(a => a.userId === athlete.id);
            if (isInTeam) return false;
            if (!term) return true;
            return (
                athlete.fullName.toLowerCase().includes(term) ||
                (athlete.nif && athlete.nif.toLowerCase().includes(term)) ||
                getEmailBase(athlete.email).toLowerCase().includes(term)
            );
        });
    }, [availableAthletes, athleteSearch, teamData]);

    const toggleAthleteSelection = (athleteProfileId) => {
        setSelectedAthletes(prev =>
            prev.includes(athleteProfileId)
                ? prev.filter(id => id !== athleteProfileId)
                : [...prev, athleteProfileId]
        );
    };

    const handleAddAthletes = async () => {
        if (selectedAthletes.length === 0) {
            alert('Por favor, selecione pelo menos um atleta.');
            return;
        }
        setSubmitting(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/api/teams/${teamData.id}/athletes`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ athleteProfileIds: selectedAthletes })
            });
            if (response.ok) {
                alert('Atletas adicionados com sucesso!');
                setSelectedAthletes([]);
                setAddingAthletes(false);
                if (onTeamUpdate) onTeamUpdate();
            } else {
                const error = await response.json();
                alert(error.message || 'Erro ao adicionar atletas');
            }
        } catch (error) {
            console.error('Error adding athletes:', error);
            alert('Erro ao adicionar atletas');
        } finally {
            setSubmitting(false);
        }
    };

    const handleRemoveAthlete = async (athleteProfileId) => {
        if (!window.confirm('Tem a certeza que deseja remover este atleta da equipa?')) return;
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/api/teams/${teamData.id}/athletes/${athleteProfileId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                alert('Atleta removido com sucesso!');
                if (onTeamUpdate) onTeamUpdate();
            } else {
                const error = await response.json();
                alert(error.message || 'Erro ao remover atleta');
            }
        } catch (error) {
            console.error('Error removing athlete:', error);
            alert('Erro ao remover atleta');
        }
    };

    const handleUpdateJerseyNumber = async (teamId, athleteId, newJerseyNumber) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/api/teams/${teamId}/athletes/${athleteId}/jersey`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ jerseyNumber: newJerseyNumber ? parseInt(newJerseyNumber) : null })
            });

            if (response.ok) {
                if (onTeamUpdate) onTeamUpdate();
            } else {
                const error = await response.json();
                alert(error.message || 'Erro ao atualizar número');
            }
        } catch (error) {
            console.error('Error updating jersey:', error);
            alert('Erro ao atualizar número');
        }
    };

    if (!isOpen || !teamData) return null;

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    return (
        <div className="modal-overlay" onMouseDown={handleOverlayClick}>
            <div className={`modal-content team-modal-content ${addingAthletes ? 'expanded' : ''}`} onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2><i className="fas fa-users"></i> {teamData.name} - Plantel Completo</h2>
                    <button className="close-btn" onClick={onClose}>
                        <FaTimes />
                    </button>
                </div>

                <div className="team-modal-body-container">
                    <div className="team-modal-body">
                        {/* Coaches Section */}
                        <div className="team-section">
                            <h3><i className="fas fa-chalkboard-teacher"></i> Equipa Técnica</h3>
                            <div className="team-list">
                                {teamData.coaches?.map((coach) => (
                                    <div className="team-list-item" key={`coach-${coach.id}`}>
                                        <div className="member-avatar coach-avatar">
                                            {coach.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                                        </div>
                                        <div className="member-info">
                                            <h4>{coach.name}</h4>
                                            <p>Treinador</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Athletes Section */}
                        <div className="team-section">
                            <div className="team-section-header">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                                    <h3><i className="fas fa-running"></i> Atletas</h3>
                                    {athletePayments && (() => {
                                        const athletes = teamData.athletes || [];
                                        const emDia = athletes.filter(a => athletePayments[a.userId]?.status === 'EmDia').length;
                                        const emAtraso = athletes.filter(a => athletePayments[a.userId]?.status === 'EmAtraso').length;
                                        return (
                                            <div style={{ display: 'inline-flex', gap: '8px', fontSize: '0.78rem' }}>
                                                <span style={{ color: '#10b981', background: 'rgba(16,185,129,0.12)', padding: '2px 8px', borderRadius: '12px', fontWeight: 600 }}>
                                                    <i className="fas fa-check-circle" style={{ marginRight: '4px' }}></i>{emDia} Em dia
                                                </span>
                                                {emAtraso > 0 && (
                                                    <span style={{ color: '#ef4444', background: 'rgba(239,68,68,0.12)', padding: '2px 8px', borderRadius: '12px', fontWeight: 600 }}>
                                                        <i className="fas fa-exclamation-circle" style={{ marginRight: '4px' }}></i>{emAtraso} Em atraso
                                                    </span>
                                                )}
                                            </div>
                                        );
                                    })()}
                                </div>
                                {isEditable && !addingAthletes && (
                                    <button className="add-athlete-btn" onClick={() => setAddingAthletes(true)}>
                                        <FaPlus /> Adicionar Atletas
                                    </button>
                                )}
                            </div>
                            <div className="team-list">
                                {teamData.athletes?.map((athlete) => (
                                    <div className="team-list-item" key={`athlete-${athlete.id}`}>
                                        <div className="member-avatar athlete-avatar">
                                            {athlete.jerseyNumber || athlete.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                                        </div>
                                        <div className="member-info">
                                            <h4>{athlete.name}</h4>
                                            <p style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px' }}>
                                                <span>{athlete.position || 'Atleta'}</span>
                                                {athlete.isCaptain && <span className="captain-badge">• Capitão</span>}
                                                {isEditable ? (
                                                    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                                                        • #
                                                        <input
                                                            type="number"
                                                            defaultValue={athlete.jerseyNumber || ''}
                                                            onBlur={(e) => {
                                                                const val = e.target.value;
                                                                if (val != athlete.jerseyNumber) {
                                                                    handleUpdateJerseyNumber(teamData.id, athlete.athleteProfileId || athlete.id, val);
                                                                }
                                                            }}
                                                            style={{ width: '45px', padding: '2px 4px', fontSize: '0.85rem', border: '1px solid #cbd5e1', borderRadius: '4px', marginLeft: '4px' }}
                                                            title="Editar número"
                                                        />
                                                    </span>
                                                ) : (
                                                    athlete.jerseyNumber && <span>• #{athlete.jerseyNumber}</span>
                                                )}
                                            </p>
                                        </div>
                                        {athletePayments && (() => {
                                            const pay = athletePayments[athlete.userId];
                                            if (!pay) return null;
                                            const cfg = pay.status === 'EmDia'
                                                ? { color: '#10b981', bg: 'rgba(16,185,129,0.12)', icon: 'fa-check-circle', label: 'Em dia', title: 'Pagamento em dia' }
                                                : pay.status === 'EmAtraso'
                                                    ? { color: '#ef4444', bg: 'rgba(239,68,68,0.12)', icon: 'fa-exclamation-circle', label: 'Em atraso', title: `Quotas em atraso: ${pay.monthsOverdue} ${pay.monthsOverdue === 1 ? 'mês' : 'meses'}` }
                                                    : { color: '#94a3b8', bg: 'rgba(148,163,184,0.15)', icon: 'fa-question-circle', label: 'N/D', title: 'Sem perfil de sócio' };
                                            return (
                                                <div
                                                    className="athlete-payment-badge"
                                                    title={cfg.title}
                                                    style={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: '5px',
                                                        padding: '4px 10px',
                                                        borderRadius: '999px',
                                                        fontSize: '0.75rem',
                                                        fontWeight: 600,
                                                        color: cfg.color,
                                                        background: cfg.bg,
                                                        whiteSpace: 'nowrap',
                                                        marginLeft: 'auto',
                                                        marginRight: isEditable ? '10px' : '0',
                                                        flexShrink: 0
                                                    }}
                                                >
                                                    <i className={`fas ${cfg.icon}`}></i>
                                                    <span>{cfg.label}</span>
                                                </div>
                                            );
                                        })()}
                                        {isEditable && (
                                            <button
                                                className="remove-athlete-btn"
                                                onClick={() => handleRemoveAthlete(athlete.athleteProfileId || athlete.id)}
                                                title="Remover Atleta"
                                            >
                                                <FaTrash />
                                            </button>
                                        )}
                                    </div>
                                ))}
                                {teamData.athletes?.length === 0 && (
                                    <div className="no-athletes-msg">Sem atletas na equipa.</div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Add Athletes Panel */}
                    {isEditable && addingAthletes && (
                        <div className="add-athletes-panel">
                            <div className="add-athletes-header">
                                <h3>Adicionar Atletas</h3>
                                <button className="close-panel-btn" onClick={() => { setAddingAthletes(false); setSelectedAthletes([]); setAthleteSearch(''); }}>
                                    <FaTimes />
                                </button>
                            </div>

                            <div className="add-athletes-search">
                                <FaSearch className="search-icon" />
                                <input
                                    type="text"
                                    placeholder="Pesquisar por nome, NIF ou email..."
                                    value={athleteSearch}
                                    onChange={(e) => setAthleteSearch(e.target.value)}
                                />
                            </div>

                            <div className="available-athletes-list">
                                {athletesLoading ? (
                                    <div className="loading-athletes">A carregar atletas...</div>
                                ) : filteredAthletes.length > 0 ? (
                                    filteredAthletes.map(athlete => {
                                        const isSelected = selectedAthletes.includes(athlete.athleteProfileId);
                                        return (
                                            <div
                                                key={athlete.id}
                                                className={`available-athlete-item ${isSelected ? 'selected' : ''}`}
                                                onClick={() => toggleAthleteSelection(athlete.athleteProfileId)}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={() => { }} // Handled by div click
                                                    className="athlete-checkbox"
                                                />
                                                <div className="available-athlete-info">
                                                    <strong>{athlete.fullName}</strong>
                                                    <span>{athlete.nif ? `NIF: ${athlete.nif}` : 'Sem NIF'} | {getEmailBase(athlete.email)}</span>
                                                </div>
                                            </div>
                                        );
                                    })
                                ) : (
                                    <div className="no-athletes-found">Nenhum atleta encontrado.</div>
                                )}
                            </div>

                            <div className="add-athletes-actions">
                                <span className="selected-count">
                                    {selectedAthletes.length} {selectedAthletes.length === 1 ? 'selecionado' : 'selecionados'}
                                </span>
                                <button
                                    className="confirm-add-btn"
                                    onClick={handleAddAthletes}
                                    disabled={selectedAthletes.length === 0 || submitting}
                                >
                                    {submitting ? 'A Adicionar...' : 'Adicionar Selecionados'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TeamDetailsModal;
